import React, {useState} from 'react';
import {toast} from 'react-toastify';

import {Button, CheckBox} from '@/components';
import type {UserModalProps} from '@/components/DeactivateAccount/types';
import Modal from '@/components/Modals';
import {kycService} from '@/services/kycService';
import {AfriexVerificationStatus} from '@/types';
import {AfriexComplianceDocumentTypes} from '@/types/AfriexComplianceDocumentTypes';
import {getErrorMessage} from '@/utils/errorMessage';
import {logger} from '@/utils/logger';

const complianceOptions = Object.values(AfriexComplianceDocumentTypes);

const ComplianceChecklistModal: React.FC<
  React.PropsWithChildren<UserModalProps>
> = ({isOpen, onClose, user}) => {
  const [selectedOptions, setSelectedOptions] = useState<
    AfriexComplianceDocumentTypes[]
  >([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const handleCheckboxChange = (option: AfriexComplianceDocumentTypes) => {
    setSelectedOptions((prevState) =>
      prevState.includes(option)
        ? prevState.filter((item) => item !== option)
        : [...prevState, option],
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedOptions.length < 1) {
      toast.warn('You must select at least one document');
      return;
    }
    setIsSubmitting(true);
    try {
      await kycService.updateKycStatus(
        user.id,
        AfriexVerificationStatus.ReuploadRequested,
        {
          requestedAdditionalDocuments: selectedOptions,
        },
      );
      toast.success('Documents request successfully submitted.');
      onClose();
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error) {
      logger.error('Error updating kyc status:', error);
      toast.error(
        'Failed to request additional documents: ' + getErrorMessage(error, ''),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      width="w-10/12 sm:w-10/12 md:w-7/12 lg:w-4/12"
      closeOnOutsideClick
      title="Request Additional Documents"
      titleSize="text-sm"
    >
      <form onSubmit={handleSubmit}>
        <p className="flex gap-2 justify-center text-sm text-center mb-2">
          Select the documents you require from the customer from the list below
        </p>

        <div className="mt-4">
          {complianceOptions.map((option) => (
            <div key={option}>
              <div className="flex items-center">
                <CheckBox
                  label={option
                    .replace(/_/g, ' ')
                    .replace(/\b\w/g, (char) => char.toUpperCase())}
                  type="checkbox"
                  checked={selectedOptions.includes(option)}
                  onChange={() => handleCheckboxChange(option)}
                  id={option}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="flex w-full justify-end space-x-2 py-5">
          <Button colorScheme="cyan" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            colorScheme="cyan"
            disabled={isSubmitting || selectedOptions.length < 1}
          >
            {isSubmitting ? (
              <div className="flex items-center">
                <svg
                  className="animate-spin h-5 w-5 mr-3 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    className="opacity-25"
                  />
                  <path
                    d="M4 12a8 8 0 0 1 8-8m0 0a8 8 0 0 1 8 8m-8-8v8"
                    stroke="currentColor"
                    strokeWidth="4"
                    className="opacity-75"
                  />
                </svg>
                Submitting...
              </div>
            ) : (
              'Submit'
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default ComplianceChecklistModal;
